"use client";

import { memo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Grid from "@/shared/components/common/Grid";
import { Form } from "@/shared/components/common/form";
import {
  passengersSchema,
  type PassengersSchemaType,
} from "@/shared/schemas/flight/passengersSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import RenderForms from "./RenderForms";
import useSWRMutation from "swr/mutation";
import fetcher from "@/shared/libs/fetcher";
import apis from "@/configs/apis";
import { jalaliToGregorian } from "@/shared/libs/date";
import { useToast } from "@/shared/hooks/useToast";
import defaultFormData from "./defaultFormData";
import { getCookie } from "@/shared/libs/utils";
import revalidatePathAction from "@/app/actions";
import useSessionStorage from "@/shared/hooks/useSessionStorage";
import { flightGTM } from "@/shared/libs/gtm";

interface PassengerBodyProps {
  route: string;
  searchParams: SearchParams;
  profileData: ProfileResult;
  flightDetail: FlightDetail;
  recentPassengers: RecentPassenger[];
  nationality: NationalityRes;
  refundRules: RefundRule[];
}

export default memo(function PassengerBody({
  route,
  searchParams,
  profileData,
  flightDetail,
  recentPassengers,
  nationality,
  refundRules,
}: PassengerBodyProps) {
  const clientSearchParams = useSearchParams();
  const router = useRouter();

  const { adult, child, infant } = searchParams ?? {};

  const { phone, email } = profileData ?? {
    phone: "",
    email: "",
  };

  const [departFlight, returnFlight] = Object.values(flightDetail);

  const isForeign = returnFlight?.is_foreign || departFlight?.is_foreign;

  const [cachedFormData, setCachedFormData] = useSessionStorage<{
    formData: PassengersSchemaType | null;
    user: "login" | "notLogin";
    reserveKey: string;
  }>("flightCachedPassengers");

  const cachedPassengers = cachedFormData?.formData?.passengers || null;
  const { toast } = useToast();
  const userToken = getCookie("token");

  const { trigger, isMutating } = useSWRMutation(
    apis.flight.storePassengers.client,
    async (url: string, { arg }: { arg: { passengersBody: string } }) =>
      await fetcher<StorePassengersRes>(url, {
        method: "POST",
        body: arg.passengersBody,
      }),
  );

  const form = useForm({
    defaultValues: defaultFormData({
      passengers: cachedPassengers,
      flights: [
        departFlight.flight_key,
        ...(returnFlight?.flight_key ? [returnFlight?.flight_key] : []),
      ],
      price: {
        adult:
          departFlight.price_details.adult?.payable +
          (returnFlight?.price_details.adult?.payable ?? 0),

        child:
          departFlight.price_details.child?.payable +
          (returnFlight?.price_details.child?.payable ?? 0),
        infant:
          departFlight.price_details.infant?.payable +
          (returnFlight?.price_details.infant?.payable ?? 0),
      },
      email: cachedFormData?.formData?.email ?? email ?? "",
      phone: cachedFormData?.formData?.phone ?? phone ?? "",
      passengersCount: {
        adult: adult ?? "0",
        child: child ?? "0",
        infant: infant ?? "0",
      },
      isForeign,
    }),
    // @ts-ignore
    resolver: yupResolver(passengersSchema),
  });

  const { control, handleSubmit } = form;

  const onSubmit = async (val: PassengersSchemaType) => {
    const { email, phone, passengers, flights } = val;

    setCachedFormData({
      formData: val,
      user: userToken ? "login" : "notLogin",
      reserveKey: `${adult}-${child}-${infant}-${
        Array.isArray(flights) ? flights?.join("-") : flights
      }`,
    });

    const payload = JSON.stringify({
      flights,
      passengers: passengers.map((passenger) => ({
        ...passenger,
        birth_date:
            !isForeign && !passenger.isInternational
            ? passenger.birth_date.year
              ? jalaliToGregorian(
                  Object.values(passenger.birth_date)
                    .map((val) => (val.length === 1 ? `0${val}` : val))
                    .reverse()
                    .join("-"),
                )
              : ""
            : Object.values(passenger.birth_date)
                .map((val) => (val.length === 1 ? `0${val}` : val))
                .reverse()
                .join("-"),
        passport_expire: passenger.passport_expire.year
          ? isForeign || passenger.isInternational
            ? Object.values(passenger.passport_expire)
                .map((val) => (val.length === 1 ? `0${val}` : val))
                .reverse()
                .join("-")
            : Object.values(passenger.passport_expire)
                .map((val) => (val.length === 1 ? `0${val}` : val))
                .reverse()
                .join("-")
          : "",
      })),
      info: {
        email: email && email?.length > 0 ? email : "johnDoe@email.com",
        phone,
      },
    });

    const { succeed, message, results } = (
      await trigger({
        passengersBody: payload,
      })
    ).data;

    toast({
      title: message,
    });

    if (succeed) {
      flightGTM.submitForm({
        userId: phone ?? email ?? "",
        passengerDetails: val.passengers,
        contactDetails: {
          email: email ?? "",
          phone: phone ?? "",
        },
        price:
          departFlight.price_details.adult?.payable +
          (returnFlight?.price_details.adult?.payable ?? 0),
      });

      const searchParams = new URLSearchParams(clientSearchParams);

      if (searchParams.has("passengerKey")) searchParams.delete("passengerKey");

      searchParams.append("passengerKey", results.key);

      revalidatePathAction(
        `/flights/${route}/confirm?${searchParams.toString()}`,
      );

      router.push(`/flights/${route}/confirm?${searchParams.toString()}`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid className="grid-cols-12 gap-x-2">
          <Grid.GridCol className="flex flex-col col-start-2 col-end-12 max-lg:col-span-full px-2 md:px-4 max-md:pb-6">
            <RenderForms
              searchParams={searchParams}
              nationality={nationality}
              flightDetail={flightDetail}
              recentPassengers={recentPassengers}
              control={control}
              loading={isMutating}
              refundRules={refundRules}
            />
          </Grid.GridCol>
        </Grid>
      </form>
    </Form>
  );
});
